import { Truck } from 'lucide-react';
import { Badge } from '@design-system/Badge';
import { Button } from '@design-system/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@design-system/Card';
import { Image } from '@design-system/Image';
import { Link } from '@design-system/Link';
import { useCheckOrderInProgress } from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type {
  DiscountsResponse,
  SMPSubscriptionDetail,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  SMP_DISCOUNT_CODE_PATH,
  SMP_I_NEED_IT_SOONER_PATH,
} from '@/constants/paths';
import { cn } from '@/utils/styles';
import { useSMPNavigate } from '../hooks/useSMPNavigate';
import {
  getStatusBadgeClassName,
  getStatusDisplayLabel,
  formatPriceFromCents,
  formatSMPDate,
  getProductGradientClass,
  getProductImageForSMP,
  computeDiscountBreakdown,
  isKitSubscription,
  isSubscriptionCancelled,
  formatBillingPolicySupplyText,
  taxDollarsToCents,
} from '../utils/smpHelpers';

// Falls back to the line item's own image when there's no branded override —
// same fallback SMPLandingPageHeader uses for its product rows.
const renderProductImage = (title: string, image: string) =>
  getProductImageForSMP(title).length > 0 ? getProductImageForSMP(title) : image;

const formatDiscountPercentLabel = (discount: DiscountsResponse): string => {
  const val = parseFloat(discount.discountValue);
  if (discount.discountType === 'percentage') {
    return `${Math.round(val)}% off`;
  }
  return `$${val.toFixed(0)} off`;
};

interface SMPSubscriptionDetailSummaryProps {
  subscription: SMPSubscriptionDetail;
  hasOrderInProgress: boolean;
  customerDiscount?: DiscountsResponse | null;
  customerDiscounts?: DiscountsResponse[];
}

export const SMPSubscriptionDetailSummary = ({
  subscription,
  hasOrderInProgress,
  customerDiscount = null,
  customerDiscounts: customerDiscountsProp,
}: SMPSubscriptionDetailSummaryProps) => {
  const { subscription_status, line_items, next_order_datetime, transaction_details } =
    subscription;
  const { smpNavigate, withSubId } = useSMPNavigate();

  const { data: orderInProgressData } = useCheckOrderInProgress(subscription.id, {
    enabled: !!subscription.id && !hasOrderInProgress,
  });
  const isOrderNowInProgress = hasOrderInProgress || (orderInProgressData?.orderNowInProgress ?? false);

  const primaryLineItem = line_items[0];
  const planName = primaryLineItem ? primaryLineItem.product_name : 'Subscription Plan';
  const supplyText =
    formatBillingPolicySupplyText(subscription.billingPolicy) ??
    (isKitSubscription(line_items) ? 'every 6 months' : 'monthly');
  const totalPriceCents = line_items.reduce((sum, item) => sum + item.price, 0);
  const taxCents = taxDollarsToCents(transaction_details?.tax ?? 0);
  const hasTax = taxCents > 0;
  const priceSuffix = hasTax ? '/ month (incl. tax)' : '/ month + tax';

  const allDiscounts: DiscountsResponse[] = customerDiscountsProp
    ?? (customerDiscount ? [customerDiscount] : []);
  const hasDiscount = allDiscounts.length > 0;

  const discountBreakdown = computeDiscountBreakdown(allDiscounts, totalPriceCents);
  const totalDiscountCents = discountBreakdown.reduce((sum, d) => sum + d.amountCents, 0);
  const discountedPriceCents = Math.max(0, totalPriceCents - totalDiscountCents);
  const savingsCents = totalPriceCents - discountedPriceCents;

  const displayedNoDiscountPriceCents = hasTax ? totalPriceCents + taxCents : totalPriceCents;
  const displayedDiscountedPriceCents = hasTax
    ? discountedPriceCents + taxCents
    : discountedPriceCents;

  return (
    <Card data-testid="smp-detail-summaryCard">
      <CardHeader data-testid="smp-detail-summaryHeader">
        <CardTitle
          className="flex flex-row items-center justify-between typography-h2 text-primary"
          data-testid="smp-detail-summaryTitle"
        >
          <span>{planName}</span>
          <Badge
            size="sm"
            className={`capitalize ${getStatusBadgeClassName(subscription_status)}`}
            data-testid="smp-detail-summaryStatusBadge"
          >
            {getStatusDisplayLabel(subscription_status)}
          </Badge>
        </CardTitle>
      </CardHeader>

      <CardContent data-testid="smp-detail-summaryContent">
        <div className="flex flex-col gap-6">
          {hasDiscount ? (
            <div
              className="flex flex-col gap-2"
              data-testid="smp-detail-discountedPrice"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-col gap-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-1.5">
                    <span className="typography-body text-primary">
                      {formatPriceFromCents(displayedDiscountedPriceCents)}
                    </span>
                    <span className="typography-caption1 text-primary">
                      {priceSuffix}
                    </span>
                    <span className="typography-body text-tertiary line-through">
                      {formatPriceFromCents(displayedNoDiscountPriceCents)}
                    </span>
                  </div>
                  {savingsCents > 0 && (
                    <span
                      className="typography-caption2 text-success"
                      data-testid="smp-detail-savings"
                    >
                      You save{' '}
                      <span className="font-bold">
                        {formatPriceFromCents(savingsCents)}
                      </span>{' '}
                      this month
                    </span>
                  )}
                </div>
                <div
                  className="flex flex-col items-end gap-1.5 shrink-0"
                  data-testid="smp-detail-discountCodes"
                >
                  {allDiscounts.map((discount) => (
                    <Link
                      key={discount.discountCodeName}
                      to={withSubId(SMP_DISCOUNT_CODE_PATH)}
                      className="inline-flex items-baseline gap-1.5 rounded-[5px] border border-dashed border-[#009E82] bg-[#EAF2F1] px-3 py-1.5 no-underline transition-opacity hover:opacity-90"
                      data-testid={`smp-detail-discountCode-${discount.discountCodeName}`}
                    >
                      <span className="typography-caption1 text-[#009E82]">
                        {discount.discountCodeName}
                      </span>
                      <span className="text-[11px] leading-none text-tertiary">
                        {formatDiscountPercentLabel(discount)}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div
              className="flex items-end gap-0.5"
              data-testid="smp-detail-price"
            >
              <span className="typography-body text-primary">
                {formatPriceFromCents(displayedNoDiscountPriceCents)}
              </span>
              <span className="typography-caption1 text-primary">
                {priceSuffix}
              </span>
            </div>
          )}

          <div
            className="flex items-center gap-5"
            data-testid="smp-detail-nextOrder"
          >
            {primaryLineItem && (
              <div
                className={cn(
                  'relative size-24 rounded-xl overflow-hidden flex items-center justify-center shrink-0',
                  getProductGradientClass(primaryLineItem.title),
                )}
                data-testid="smp-detail-productImage"
              >
                <Image
                  src={renderProductImage(
                    primaryLineItem.title,
                    primaryLineItem.image,
                  )}
                  alt={primaryLineItem.title}
                  className="object-cover size-full"
                />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <span className="typography-body text-primary">
                Next order {formatSMPDate(next_order_datetime)}
              </span>
              <span
                className="typography-caption2 text-secondary"
                data-testid="smp-detail-supply"
              >
                {supplyText}
              </span>
            </div>
          </div>

          {!isSubscriptionCancelled(subscription_status) && (
            <div className="flex flex-col gap-2">
              {isOrderNowInProgress && (
                <div
                  className="flex items-center gap-2 rounded-md bg-[#EAF6F4] px-4 py-3"
                  data-testid="smp-detail-orderNowInProgressBanner"
                >
                  <Truck
                    className="size-4 shrink-0 text-[#146464]"
                    aria-hidden="true"
                  />
                  <span className="typography-caption1 text-[#146464]">
                    Current order is in progress. Once it&apos;s placed
                    successfully, you&apos;ll be able to order again.
                  </span>
                </div>
              )}
              <Button
                className="w-full"
                disabled={isOrderNowInProgress}
                onClick={() => smpNavigate(SMP_I_NEED_IT_SOONER_PATH)}
                data-testid="smp-detail-orderNowButton"
              >
                Order Now
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
